// (c) chaehongee. Confidential and proprietary.

import i18n from './index'

function getLocaleTag(): string {
    return i18n.language?.startsWith('ko') ? 'ko-KR' : 'en-US'
}

export function formatCurrency(value: number, currency: string): string {
    return new Intl.NumberFormat(getLocaleTag(), {
        style: 'currency',
        currency,
        minimumFractionDigits: currency === 'KRW' ? 0 : 2,
        maximumFractionDigits: currency === 'KRW' ? 0 : 2,
    }).format(value)
}

/**
 * Price-tag formatting: whole amounts show no decimals ("$4", "₩5,900"),
 * fractional amounts keep cents ("$3.20"). KRW always rounds to whole won.
 */
export function formatPriceTag(value: number, currency: string): string {
    const upper = currency.toUpperCase()
    const isKrw = upper === 'KRW'
    const isWhole = Number.isInteger(value)
    return new Intl.NumberFormat(getLocaleTag(), {
        style: 'currency',
        currency: upper,
        minimumFractionDigits: isKrw || isWhole ? 0 : 2,
        maximumFractionDigits: isKrw ? 0 : 2,
    }).format(value)
}

export function formatNumber(value: number): string {
    return new Intl.NumberFormat(getLocaleTag()).format(value)
}

export function formatDate(isoDateString: string): string {
    const date = new Date(isoDateString)
    return new Intl.DateTimeFormat(getLocaleTag(), {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(date)
}

export function formatRelativeTime(isoDateString: string): string {
    const now = new Date()
    const date = new Date(isoDateString)
    const diffMs = now.getTime() - date.getTime()
    const diffSeconds = Math.floor(diffMs / 1000)

    const locale = getLocaleTag()
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

    if (Math.abs(diffSeconds) < 60) {
        return rtf.format(-diffSeconds, 'second')
    }
    const diffMinutes = Math.floor(diffSeconds / 60)
    if (Math.abs(diffMinutes) < 60) {
        return rtf.format(-diffMinutes, 'minute')
    }
    const diffHours = Math.floor(diffMinutes / 60)
    if (Math.abs(diffHours) < 24) {
        return rtf.format(-diffHours, 'hour')
    }
    const diffDays = Math.floor(diffHours / 24)
    if (Math.abs(diffDays) < 7) {
        return rtf.format(-diffDays, 'day')
    }
    const diffWeeks = Math.floor(diffDays / 7)
    if (Math.abs(diffWeeks) < 4) {
        return rtf.format(-diffWeeks, 'week')
    }
    const diffMonths = Math.floor(diffDays / 30)
    if (Math.abs(diffMonths) < 12) {
        return rtf.format(-diffMonths, 'month')
    }
    const diffYears = Math.floor(diffDays / 365)
    return rtf.format(-diffYears, 'year')
}
